import { useState, useRef } from "react";
import { toast } from "sonner";
import api from "../../api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Plus, Trash2, Upload, Image as ImageIcon, GripVertical } from "lucide-react";
import CalloutImage from "./CalloutImage";

const uid = () => Math.random().toString(36).slice(2, 10);
const SCREEN_KEYS = [
  ["", "Nessuna (non contestuale)"], ["dashboard", "Dashboard"], ["apartments", "Appartamenti"], ["pipeline", "Pipeline Trattative"],
  ["calendar", "Calendario"], ["bookings", "Prenotazioni"], ["guests", "Ospiti"], ["cleanings", "Pulizie"], ["maintenance", "Manutenzioni"],
  ["payments", "Pagamenti"], ["owners", "Proprietari"], ["qr", "QR Center"], ["local_guide", "Local Guide"], ["owner_portal", "Portale Proprietario"],
  ["accounting", "Contabilità"], ["release", "Release Dashboard"], ["settings", "Impostazioni"], ["academy", "Academy"],
];
const CALLOUT_COLORS = ["#4f46e5", "#dc2626", "#059669", "#d97706", "#0ea5e9"];

export default function GuideEditor({ initial, meta, onBack, onSaved }) {
  const [g, setG] = useState(() => initial || {
    title: "", category: "generale", difficulty: "base", screen_key: "", audiences: ["operativo"], summary: "",
    steps: [], tips: [], common_errors: [], faqs: [], videos: [], attachments: [],
  });
  const [activeCallout, setActiveCallout] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef({});

  const set = (k, v) => setG((p) => ({ ...p, [k]: v }));
  const setStep = (sid, patch) => set("steps", g.steps.map((s) => s.id === sid ? { ...s, ...patch } : s));

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/academy/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const addStep = () => set("steps", [...g.steps, { id: uid(), number: g.steps.length + 1, title: "", text: "", image_url: "", callouts: [] }]);
  const delStep = (sid) => set("steps", g.steps.filter((s) => s.id !== sid).map((s, i) => ({ ...s, number: i + 1 })));

  const uploadStepImage = async (sid, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await upload(f); setStep(sid, { image_url: up.url }); toast.success("Immagine caricata"); }
    catch { toast.error("Errore upload"); } e.target.value = "";
  };

  const addCallout = (sid, pos) => {
    const s = g.steps.find((x) => x.id === sid);
    const cid = uid();
    const c = { id: cid, n: (s.callouts?.length || 0) + 1, shape: "dot", x: pos.x, y: pos.y, w: 18, h: 12, text: "", color: "#4f46e5" };
    setStep(sid, { callouts: [...(s.callouts || []), c] });
    setActiveCallout(cid);
  };
  const updCallout = (sid, cid, patch) => {
    const s = g.steps.find((x) => x.id === sid);
    setStep(sid, { callouts: s.callouts.map((c) => c.id === cid ? { ...c, ...patch } : c) });
  };
  const delCallout = (sid, cid) => {
    const s = g.steps.find((x) => x.id === sid);
    setStep(sid, { callouts: s.callouts.filter((c) => c.id !== cid).map((c, i) => ({ ...c, n: i + 1 })) });
  };

  // liste semplici
  const addList = (k, v) => set(k, [...(g[k] || []), v]);
  const updList = (k, i, v) => set(k, g[k].map((x, idx) => idx === i ? v : x));
  const delList = (k, i) => set(k, g[k].filter((_, idx) => idx !== i));

  const uploadAttachment = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await upload(f); addList("attachments", { id: uid(), name: f.name, url: up.url }); toast.success("Allegato caricato"); }
    catch { toast.error("Errore upload"); } e.target.value = "";
  };
  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await upload(f); addList("videos", { id: uid(), type: "upload", url: up.url, title: f.name }); toast.success("Video caricato"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Errore upload"); } e.target.value = "";
  };

  const save = async () => {
    if (!g.title.trim()) return toast.error("Titolo obbligatorio");
    try {
      setSaving(true);
      const r = g.id ? await api.put(`/academy/guides/${g.id}`, g) : await api.post("/academy/guides", g);
      toast.success("Guida salvata");
      onSaved(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 pb-24" data-testid="guide-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft size={18} /></Button>
          <h2 className="font-head text-xl font-semibold text-slate-900">{g.id ? "Modifica guida" : "Nuova guida"}</h2>
        </div>
        <Button className="gap-2" onClick={save} disabled={saving} data-testid="guide-save-btn"><Save size={15} /> Salva</Button>
      </div>

      {/* Info generali */}
      <Card className="border-slate-200 p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><label className="text-xs text-slate-500">Titolo *</label><Input value={g.title} onChange={(e) => set("title", e.target.value)} placeholder="Es. Come acquisire un nuovo immobile" data-testid="ge-title" /></div>
          <div><label className="text-xs text-slate-500">Categoria</label>
            <Select value={g.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="ge-category"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="text-xs text-slate-500">Livello difficoltà</label>
            <Select value={g.difficulty} onValueChange={(v) => set("difficulty", v)}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.difficulties || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="text-xs text-slate-500">Aiuto contestuale su schermata</label>
            <Select value={g.screen_key || "none"} onValueChange={(v) => set("screen_key", v === "none" ? "" : v)}><SelectTrigger data-testid="ge-screen"><SelectValue /></SelectTrigger>
              <SelectContent>{SCREEN_KEYS.map(([k, l]) => <SelectItem key={k || "none"} value={k || "none"}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="text-xs text-slate-500">Destinatari</label>
            <div className="mt-1 flex flex-wrap gap-2">{(meta?.audiences || []).map((a) => (
              <label key={a.key} className="flex items-center gap-1.5 text-xs">
                <Checkbox checked={g.audiences?.includes(a.key)} onCheckedChange={(v) => set("audiences", v ? [...(g.audiences || []), a.key] : g.audiences.filter((x) => x !== a.key))} />{a.label}
              </label>))}</div></div>
          <div className="sm:col-span-2"><label className="text-xs text-slate-500">Introduzione</label><Textarea value={g.summary} onChange={(e) => set("summary", e.target.value)} placeholder="Breve descrizione della guida" /></div>
        </div>
      </Card>

      {/* Passi */}
      <div className="flex items-center justify-between">
        <h3 className="font-head text-sm font-semibold text-slate-700">Passaggi numerati</h3>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={addStep} data-testid="ge-add-step"><Plus size={14} /> Aggiungi passo</Button>
      </div>
      {g.steps.map((s) => (
        <Card key={s.id} className="border-slate-200 p-5" data-testid={`ge-step-${s.number}`}>
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><GripVertical size={14} className="text-slate-300" /> Passo {s.number}</div>
            <Button size="icon" variant="ghost" className="text-red-500" onClick={() => delStep(s.id)}><Trash2 size={14} /></Button>
          </div>
          <div className="grid gap-3">
            <Input value={s.title} onChange={(e) => setStep(s.id, { title: e.target.value })} placeholder="Titolo del passo (dove cliccare)" data-testid={`ge-step-title-${s.number}`} />
            <Textarea value={s.text} onChange={(e) => setStep(s.id, { text: e.target.value })} placeholder="Istruzioni: spiega esattamente dove cliccare e cosa cercare" data-testid={`ge-step-text-${s.number}`} />
            <input ref={(el) => (fileRefs.current[s.id] = el)} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadStepImage(s.id, e)} data-testid={`ge-step-img-input-${s.number}`} />
            {!s.image_url ? (
              <Button variant="outline" size="sm" className="w-fit gap-1.5" onClick={() => fileRefs.current[s.id]?.click()} data-testid={`ge-step-upload-${s.number}`}><ImageIcon size={14} /> Carica screenshot</Button>
            ) : (
              <div className="grid gap-3 lg:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs text-slate-500">Clicca sull'immagine per aggiungere un callout numerato</p>
                  <CalloutImage src={s.image_url} callouts={s.callouts} editable onAddCallout={(pos) => addCallout(s.id, pos)} activeId={activeCallout} onSelect={(c) => setActiveCallout(c.id)} />
                  <Button variant="ghost" size="sm" className="mt-1 gap-1.5 text-slate-500" onClick={() => fileRefs.current[s.id]?.click()}><Upload size={13} /> Sostituisci</Button>
                </div>
                <div className="space-y-2">
                  {(s.callouts || []).map((c) => (
                    <div key={c.id} className={`rounded-lg border p-2 ${activeCallout === c.id ? "border-indigo-400 bg-indigo-50/40" : "border-slate-100"}`} onClick={() => setActiveCallout(c.id)}>
                      <div className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: c.color }}>{c.n}</span>
                        <Select value={c.shape} onValueChange={(v) => updCallout(s.id, c.id, { shape: v })}>
                          <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="dot">Pallino</SelectItem><SelectItem value="box">Riquadro</SelectItem><SelectItem value="arrow">Freccia</SelectItem></SelectContent>
                        </Select>
                        <div className="flex gap-1">{CALLOUT_COLORS.map((col) => <button key={col} onClick={() => updCallout(s.id, c.id, { color: col })} className="h-4 w-4 rounded-full ring-1 ring-white" style={{ backgroundColor: col, outline: c.color === col ? `2px solid ${col}` : "none" }} />)}</div>
                        <Button size="icon" variant="ghost" className="ml-auto h-6 w-6 text-red-500" onClick={() => delCallout(s.id, c.id)}><Trash2 size={12} /></Button>
                      </div>
                      <Input className="mt-1.5 h-8 text-sm" value={c.text} onChange={(e) => updCallout(s.id, c.id, { text: e.target.value })} placeholder={`Descrizione callout ${c.n}`} />
                    </div>
                  ))}
                  {(s.callouts || []).length === 0 && <p className="text-xs text-slate-400">Nessun callout. Clicca sull'immagine per aggiungerne.</p>}
                </div>
              </div>
            )}
          </div>
        </Card>
      ))}

      {/* Suggerimenti / Errori / FAQ */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ListCard title="Suggerimenti" testid="ge-tips" items={g.tips} onAdd={() => addList("tips", "")} onDel={(i) => delList("tips", i)}
          render={(t, i) => <Input value={t} onChange={(e) => updList("tips", i, e.target.value)} placeholder="Suggerimento" />} />
        <ListCard title="Errori comuni" testid="ge-errors" items={g.common_errors} onAdd={() => addList("common_errors", { problem: "", solution: "" })} onDel={(i) => delList("common_errors", i)}
          render={(e, i) => <div className="grid gap-1.5"><Input value={e.problem} onChange={(ev) => updList("common_errors", i, { ...e, problem: ev.target.value })} placeholder="Problema" /><Input value={e.solution} onChange={(ev) => updList("common_errors", i, { ...e, solution: ev.target.value })} placeholder="Soluzione" /></div>} />
      </div>
      <ListCard title="FAQ" testid="ge-faqs" items={g.faqs} onAdd={() => addList("faqs", { q: "", a: "" })} onDel={(i) => delList("faqs", i)}
        render={(f, i) => <div className="grid gap-1.5"><Input value={f.q} onChange={(ev) => updList("faqs", i, { ...f, q: ev.target.value })} placeholder="Domanda" /><Textarea value={f.a} onChange={(ev) => updList("faqs", i, { ...f, a: ev.target.value })} placeholder="Risposta" /></div>} />

      {/* Video + Allegati */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-slate-200 p-5">
          <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold text-slate-700">Video</span>
            <div className="flex gap-1"><input ref={(el) => (fileRefs.current.video = el)} type="file" accept=".mp4,.webm,.mov" className="hidden" onChange={uploadVideo} data-testid="ge-video-input" />
              <Button size="sm" variant="outline" className="gap-1" onClick={() => fileRefs.current.video?.click()}><Upload size={13} /> Upload</Button></div>
          </div>
          <VideoAdder onAdd={(v) => addList("videos", v)} />
          <div className="mt-2 space-y-1.5">{(g.videos || []).map((v, i) => <div key={v.id} className="flex items-center justify-between gap-2 rounded border border-slate-100 p-1.5 text-sm"><span className="truncate">{v.type === "upload" ? "📹" : "🔗"} {v.title || v.url}</span><Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => delList("videos", i)}><Trash2 size={12} /></Button></div>)}</div>
        </Card>
        <Card className="border-slate-200 p-5">
          <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold text-slate-700">Allegati (PDF/manuali)</span>
            <input ref={(el) => (fileRefs.current.att = el)} type="file" className="hidden" onChange={uploadAttachment} data-testid="ge-att-input" />
            <Button size="sm" variant="outline" className="gap-1" onClick={() => fileRefs.current.att?.click()}><Upload size={13} /> Carica</Button>
          </div>
          <div className="space-y-1.5">{(g.attachments || []).map((a, i) => <div key={a.id} className="flex items-center justify-between gap-2 rounded border border-slate-100 p-1.5 text-sm"><span className="truncate">📎 {a.name}</span><Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => delList("attachments", i)}><Trash2 size={12} /></Button></div>)}</div>
        </Card>
      </div>

      <div className="flex justify-end"><Button className="gap-2" onClick={save} disabled={saving}><Save size={15} /> Salva guida</Button></div>
    </div>
  );
}

function ListCard({ title, testid, items, onAdd, onDel, render }) {
  return (
    <Card className="border-slate-200 p-5" data-testid={testid}>
      <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold text-slate-700">{title}</span>
        <Button size="sm" variant="outline" className="gap-1" onClick={onAdd}><Plus size={13} /> Aggiungi</Button></div>
      <div className="space-y-2">
        {(items || []).map((it, i) => <div key={i} className="flex items-start gap-2"><div className="flex-1">{render(it, i)}</div><Button size="icon" variant="ghost" className="text-red-500" onClick={() => onDel(i)}><Trash2 size={14} /></Button></div>)}
        {(items || []).length === 0 && <p className="text-xs text-slate-400">Nessuna voce.</p>}
      </div>
    </Card>
  );
}

function VideoAdder({ onAdd }) {
  const [url, setUrl] = useState(""); const [title, setTitle] = useState("");
  return (
    <div className="flex gap-1">
      <Input className="h-8" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo" />
      <Input className="h-8" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Link YouTube/Vimeo/Drive" data-testid="ge-video-url" />
      <Button size="sm" variant="outline" onClick={() => { if (url) { onAdd({ id: uid(), type: "external", url, title, provider: "external" }); setUrl(""); setTitle(""); } }}><Plus size={14} /></Button>
    </div>
  );
}
